export interface FoodSubscription {
  id: string;
  name: string;
  zipCodes: string[];
  promotion?: string[];
  address?: {
    street: string;
    city: string;
    state: string;
    zip: string;
  };
  phoneNumber?: string;
  webLink?: string;
  claimInstructions?: string;
  // Add other properties as needed based on your DynamoDB table structure
}

export interface FoodSubscriptionResponse {
  success: boolean;
  data?: FoodSubscription[];
  error?: string;
  details?: string;
}

export class FoodSubscriptionService {
  private readonly lambdaUrl = 'https://s2xyibaz4mfeajm2xmb3fevfxy0twowm.lambda-url.us-east-2.on.aws/';

  /**
   * Get food subscriptions available for a specific zip code
   * @param zipCode - The zip code to search for
   * @returns Promise<FoodSubscription[]> - Array of available food subscriptions
   */
  async getFoodSubscriptionsByZipCode(zipCode: string): Promise<FoodSubscription[]> {
    if (!zipCode || zipCode.trim() === '') {
      throw new Error('Zip code is required');
    }

    try {
      const url = new URL(this.lambdaUrl);
      url.searchParams.append('zipCode', zipCode.trim());

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: FoodSubscriptionResponse = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch food subscriptions');
      }

      return result.data || [];
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to fetch food subscriptions: ${error.message}`);
      }
      throw new Error('Failed to fetch food subscriptions: Unknown error');
    }
  }

  /**
   * Check if food subscriptions are available for a specific zip code
   * @param zipCode - The zip code to check
   * @returns Promise<boolean> - True if subscriptions are available
   */
  async isZipCodeServiceable(zipCode: string): Promise<boolean> {
    try {
      const subscriptions = await this.getFoodSubscriptionsByZipCode(zipCode);
      return subscriptions.length > 0;
    } catch {
      return false;
    }
  }

  /**
   * Get the count of available subscriptions for a zip code
   * @param zipCode - The zip code to check
   * @returns Promise<number> - Number of available subscriptions
   */
  async getSubscriptionCount(zipCode: string): Promise<number> {
    try {
      const subscriptions = await this.getFoodSubscriptionsByZipCode(zipCode);
      return subscriptions.length;
    } catch {
      return 0;
    }
  }
}

// Export a singleton instance for convenience
export const foodSubscriptionService = new FoodSubscriptionService();
